import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { LOCALES } from '@chess-uno/shared-protocol'
import { useI18n } from './i18n/index.js'
import { APP_META, MARKDOWN_PATHS, OG_LOCALES, PAGE_META, PAGE_PATHS, publicPageAt, siteUrl, structuredData, X_DEFAULT } from './seo.js'

function setMeta(name: string, content: string, property = false) {
  const attribute = property ? 'property' : 'name'
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, name)
    document.head.append(element)
  }
  element.content = content
}

/** hreflang links between the language versions of a public page (removed on the other pages). */
function setAlternates(urls: Record<string, string> | null) {
  for (const link of document.head.querySelectorAll('link[rel="alternate"][hreflang]')) link.remove()
  if (!urls) return
  for (const [lang, href] of Object.entries(urls)) {
    const link = document.createElement('link')
    link.rel = 'alternate'
    link.hreflang = lang
    link.href = href
    document.head.append(link)
  }
}

/** The page's other languages, for link previews (og:locale:alternate). */
function setOgAlternates(locale: string) {
  for (const meta of document.head.querySelectorAll('meta[property="og:locale:alternate"]')) meta.remove()
  for (const lang of LOCALES) {
    if (lang === locale) continue
    const meta = document.createElement('meta')
    meta.setAttribute('property', 'og:locale:alternate')
    meta.content = OG_LOCALES[lang]
    document.head.append(meta)
  }
}

/** Markdown version of the page, for LLMs (removed on pages without one). */
function setMarkdown(href: string | null) {
  document.head.querySelector('link[rel="alternate"][type="text/markdown"]')?.remove()
  if (!href) return
  const link = document.createElement('link')
  link.rel = 'alternate'
  link.type = 'text/markdown'
  link.href = href
  document.head.append(link)
}

export function SeoHead() {
  const { pathname } = useLocation()
  const { locale } = useI18n()
  useEffect(() => {
    const page = publicPageAt(pathname)
    const meta = page ? PAGE_META[page.key][page.locale] : APP_META[locale]
    document.title = meta.title
    setMeta('robots', page ? 'index,follow' : 'noindex,follow')
    setMeta('description', meta.description)
    setMeta('og:title', meta.title, true)
    setMeta('og:description', meta.description, true)
    setMeta('og:type', 'website', true)
    setMeta('og:locale', OG_LOCALES[locale], true)
    setOgAlternates(locale)
    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]') ?? document.createElement('link')
    if (page) {
      const url = `${siteUrl}${pathname}`
      canonical.rel = 'canonical'
      canonical.href = url
      document.head.append(canonical)
      setMeta('og:url', url, true)
      const urls: Record<string, string> = {}
      for (const lang of LOCALES) urls[lang] = `${siteUrl}${PAGE_PATHS[page.key][lang]}`
      urls['x-default'] = `${siteUrl}${PAGE_PATHS[page.key][X_DEFAULT]}`
      setAlternates(urls)
    } else {
      canonical.remove()
      document.head.querySelector('meta[property="og:url"]')?.remove()
      setAlternates(null)
    }
    const markdown = page ? MARKDOWN_PATHS[page.key]?.[page.locale] : undefined
    setMarkdown(markdown ? `${siteUrl}${markdown}` : null)
    const existingData = document.getElementById('seo-structured-data')
    const schema = page ? structuredData(page.key, page.locale) : []
    if (schema.length > 0) {
      const data = existingData ?? document.createElement('script')
      data.id = 'seo-structured-data'
      data.setAttribute('type', 'application/ld+json')
      data.textContent = JSON.stringify(schema).replace(/</g, '\\u003c')
      document.head.append(data)
    } else {
      existingData?.remove()
    }
  }, [pathname, locale])
  return null
}
